import React from 'react'
import { Link } from 'react-router-dom'
import { getBoatType, getBoatStatus } from '../lib/utils'

export default function BoatCard({ boat, layout = 'grid' }) {
  if (!boat) return null

  const boatType = getBoatType(boat)
  const boatStatus = getBoatStatus(boat)

  // Ensure image URL is optimized using Cloudflare CDN if applicable
  const rawImage =
    Array.isArray(boat.billede) && boat.billede.length > 0
      ? boat.billede[0]
      : 'https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/nordfyn-marine-logo.png'

  let imageUrl = rawImage
  if (rawImage.includes('assets.auxodev.dk') && !rawImage.includes('/cdn-cgi/image/')) {
    imageUrl = rawImage.replace(
      'assets.auxodev.dk/',
      'assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/'
    )
  }

  // Get slug from url_slug or system slug
  const rawSlug = boat.url_slug || (typeof boat.slug === 'object' ? boat.slug._ : boat.slug) || boat.id
  const boatSlug = String(rawSlug || '').trim()

  const handleBoatClick = () => {
    try {
      if (window.scrollY > 50) {
        sessionStorage.setItem('nordfyn_boats_scroll_pos', String(window.scrollY))
      }
    } catch (e) {}
  }

  // Status color styles
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Nyhed':
        return 'bg-blue-600 text-white'
      case 'Til salg':
        return 'bg-emerald-600 text-white'
      case 'Nedsat':
        return 'bg-amber-600 text-white'
      case 'Reserveret':
        return 'bg-purple-600 text-white'
      case 'Solgt':
        return 'bg-slate-700 text-slate-200'
      default:
        return 'bg-[#1E7BC1] text-white'
    }
  }

  // ROW LAYOUT (Horizontal split: Large image on left, detailed info on right)
  if (layout === 'row') {
    // Secondary image for inset strip if available
    const rawSecondaryImage =
      Array.isArray(boat.billede) && boat.billede.length > 1
        ? boat.billede[1]
        : rawImage

    let secondaryImageUrl = rawSecondaryImage
    if (rawSecondaryImage.includes('assets.auxodev.dk') && !rawSecondaryImage.includes('/cdn-cgi/image/')) {
      secondaryImageUrl = rawSecondaryImage.replace(
        'assets.auxodev.dk/',
        'assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/'
      )
    }

    return (
      <div className="group relative w-full flex flex-col lg:flex-row items-stretch my-4 sm:my-6 font-sans">
        {/* Left Side: Hero / Background Image */}
        <div className="w-full lg:w-2/3 h-[320px] sm:h-[420px] lg:h-[550px] relative overflow-hidden shadow-md bg-slate-900 shrink-0 rounded-2xl">
          <Link to={`/baad/${boatSlug}`} onClick={handleBoatClick} className="block w-full h-full relative">
            <img
              src={imageUrl}
              alt={boat.title || 'Båd'}
              className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            {/* Dark gradient vignette overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 pointer-events-none" />

            {/* Status & Category Badges */}
            <div className="absolute top-4 left-4 z-10 flex flex-wrap gap-2">
              {boatStatus && (
                <span
                  className={`inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] shadow-sm rounded-full ${getStatusBadge(
                    boatStatus
                  )}`}
                >
                  {boatStatus}
                </span>
              )}
              {boat.sagsnummer && (
                <span className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] bg-slate-900/80 backdrop-blur-sm text-amber-300 border border-amber-500/40 rounded-full">
                  Sagsnr. {boat.sagsnummer}
                </span>
              )}
              {boatType && (
                <span className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] bg-[#071320]/80 backdrop-blur-sm text-white border border-slate-700/50 rounded-full">
                  {boatType}
                </span>
              )}
            </div>
          </Link>
        </div>

        {/* Right Side: Floating / Overlapping Spec Card */}
        <div className="w-[90%] sm:w-[80%] mx-auto lg:w-5/12 lg:mx-0 lg:-ml-16 -mt-8 sm:-mt-12 lg:mt-auto lg:mb-auto z-10 bg-white shadow-xl border border-slate-200 flex flex-col justify-between overflow-hidden relative rounded-2xl">
          
          {/* Top Section with Content and Nautical Badge */}
          <div className="flex justify-between items-start relative">
            {/* Text Content Area */}
            <div className="p-6 sm:p-8 pb-4 flex-1">
              {/* Brand Header */}
              <div className="pr-12">
                <h4 className="text-[10px] font-bold tracking-[0.25em] text-[#1E7BC1] uppercase">
                  NORDFYN MARINE
                </h4>
                <h3 className="text-base sm:text-xl font-serif font-bold tracking-wider text-[#071320] uppercase mt-1 group-hover:text-[#1E7BC1] transition-colors">
                  <Link to={`/baad/${boatSlug}`} onClick={handleBoatClick}>{boat.title}</Link>
                </h3>
                {boat.aargang && (
                  <p className="text-[10px] tracking-widest text-slate-400 uppercase mt-0.5">
                    ÅRGANG {boat.aargang}
                  </p>
                )}
              </div>

              {/* Key Specs Key-Value Table */}
              <div className="space-y-1.5 pt-4 mt-4 border-t border-slate-100 max-w-sm text-[11px]">
                {boat.sagsnummer && (
                  <div className="grid grid-cols-[100px_1fr] items-center">
                    <span className="text-slate-400 font-medium tracking-widest uppercase text-[10px]">
                      SAGSNR.
                    </span>
                    <span className="text-[#071320] font-semibold tracking-wider uppercase font-mono">
                      {boat.sagsnummer}
                    </span>
                  </div>
                )}
                {boat.dimensioner && (
                  <div className="grid grid-cols-[100px_1fr] items-center">
                    <span className="text-slate-400 font-medium tracking-widest uppercase text-[10px]">
                      LÆNGDE
                    </span>
                    <span className="text-[#071320] font-semibold tracking-wider uppercase font-mono">
                      {boat.dimensioner}
                    </span>
                  </div>
                )}

                {boat.aargang && (
                  <div className="grid grid-cols-[100px_1fr] items-center">
                    <span className="text-slate-400 font-medium tracking-widest uppercase text-[10px]">
                      ÅRGANG
                    </span>
                    <span className="text-[#071320] font-semibold tracking-wider uppercase font-mono">
                      {boat.aargang}
                    </span>
                  </div>
                )}

                {boat.placering && (
                  <div className="grid grid-cols-[100px_1fr] items-center">
                    <span className="text-slate-400 font-medium tracking-widest uppercase text-[10px]">
                      HAVN
                    </span>
                    <span className="text-[#071320] font-semibold tracking-wider uppercase">
                      {boat.placering}
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-[100px_1fr] items-center pt-2 mt-2 border-t border-slate-100">
                  <span className="text-slate-400 font-medium tracking-widest uppercase text-[10px]">
                    PRIS
                  </span>
                  <span className="text-[#1E7BC1] font-serif font-bold text-2xl sm:text-3xl tracking-wider">
                    {boat.pris || 'Kontakt for pris'}
                  </span>
                </div>
              </div>
            </div>

            {/* Nautical Compass Badge (Top Right) */}
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#CDE3ED] flex items-center justify-center shrink-0 absolute top-0 right-0 z-10 border-l border-b border-slate-200/60 rounded-bl-2xl">
              <i className="fa-solid fa-compass text-2xl text-[#1E7BC1]" aria-hidden="true" />
            </div>
          </div>

          {/* Card Footer / Framed Action Button */}
          <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-t border-slate-100/80 bg-slate-50/50">
            {/* Outline CTA Button */}
            <div>
              <Link
                to={`/baad/${boatSlug}`}
                onClick={handleBoatClick}
                className="inline-block px-7 py-3 border border-slate-300 text-[11px] font-bold tracking-[0.2em] text-[#071320] uppercase bg-white hover:bg-[#1E7BC1] hover:text-white hover:border-[#1E7BC1] transition-all duration-200 shadow-none rounded-xl"
              >
                SE DETALJER
              </Link>
            </div>
          </div>

        </div>
      </div>
    )
  }

  // DEFAULT GRID LAYOUT
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-none hover:border-[#1E7BC1] transition-all duration-300 flex flex-col h-full relative">
      <Link to={`/baad/${boatSlug}`} onClick={handleBoatClick} className="block relative h-72 sm:h-80 md:h-96 lg:h-[420px] w-full overflow-hidden bg-slate-100">
        <img
          src={imageUrl}
          alt={boat.title || 'Båd'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />

        {/* Status Badge */}
        {boatStatus && (
          <div className="absolute top-3 left-3 z-10">
            <span
              className={`inline-block px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] rounded-full ${getStatusBadge(
                boatStatus
              )}`}
            >
              {boatStatus}
            </span>
          </div>
        )}

        {/* Category & Sagsnr Label Overlay */}
        <div className="absolute bottom-3 left-3 z-10 flex flex-wrap gap-1.5">
          {boatType && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold tracking-[0.15em] uppercase bg-[#2BABE3] text-white rounded-full">
              <i className="fa-solid fa-anchor text-[9px]" aria-hidden="true" />
              {boatType}
            </span>
          )}
          {boat.sagsnummer && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-semibold tracking-[0.15em] uppercase bg-slate-900/80 backdrop-blur-sm text-amber-300 border border-amber-500/30 rounded-full">
              Sagsnr. {boat.sagsnummer}
            </span>
          )}
        </div>
      </Link>

      <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
        <div>
          <div className="flex justify-between items-start gap-2 mb-2">
            <h3 className="font-serif font-normal text-lg sm:text-xl text-slate-900 group-hover:text-[#1E7BC1] transition-colors line-clamp-1 tracking-tight">
              <Link to={`/baad/${boatSlug}`} onClick={handleBoatClick}>{boat.title}</Link>
            </h3>
            {boat.aargang && (
              <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 border border-slate-200 tracking-widest shrink-0 uppercase rounded-full">
                {boat.aargang}
              </span>
            )}
          </div>

          <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed font-light">
            {boat.kort_beskrivelse || boat.maerke_model}
          </p>
        </div>

        <div className="pt-2">
          {/* Key Quick Specs Divider Line */}
          {boat.dimensioner && (
            <div className="text-[11px] text-slate-500 mb-4 pb-3 border-b border-slate-100 flex items-center justify-between font-light">
              <span className="uppercase text-[9px] tracking-[0.2em] text-slate-400 font-medium">Dimensioner</span>
              <span>{boat.dimensioner}</span>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <div>
              <span className="text-[9px] text-slate-400 block uppercase tracking-[0.2em] font-medium">Pris</span>
              <span className="font-serif font-bold text-xl sm:text-2xl text-[#1E7BC1]">
                {boat.pris || 'Kontakt for pris'}
              </span>
            </div>

            <Link
              to={`/baad/${boatSlug}`}
              onClick={handleBoatClick}
              className="inline-flex items-center gap-2 text-[10px] uppercase font-semibold tracking-[0.2em] text-[#1E7BC1] group-hover:text-[#2BABE3] transition-colors border border-slate-300 group-hover:border-[#2BABE3] px-3.5 py-2 rounded-xl"
            >
              <span>Se detaljer</span>
              <i className="fa-solid fa-arrow-right text-[9px] group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
